import * as React from "react";
import { ThunkDispatch } from "../../../types/redux";

import classnames from "classnames";
import { Box, Flex } from "@rebass/grid";
import {
  Button,
  Text,
  HTMLTable,
  Classes,
  InputGroup,
} from "@blueprintjs/core";
import { createOutputTag, getOutputTags } from "src/store/modules/output_tags";
import { RootState } from "src/store/reducer";
import connect from "react-redux/es/components/connect";
import { RouteComponentProps, withRouter } from "react-router-dom";
import Loading from "src/components/molecules/Loading";
import { IconNames } from "@blueprintjs/icons";
import { find, map, toLower } from "lodash";
import OutputTagsRow from "./OutputTagsRow";
import FloatingButton from "src/components/atoms/FloatingButton";
import AddOutputTagModal from "./AddOutputTagModal";
import { OutputTag } from "src/types/models";

const textClasses = classnames(Classes.TEXT_MUTED, Classes.TEXT_LARGE);
interface IProps extends RouteComponentProps<any> {
  outputTags: OutputTag[];
  dispatch?: ThunkDispatch;
  clientId: number;
}

interface IState {
  isCreateModalOpen: boolean;
  isLoading: boolean;
  isAddLoading: boolean;
  isFilterOpen: boolean;
  outputFilter: string;
  isAdding: boolean;
  filteredOutputTags: OutputTag[];
}

class OutputsTab extends React.Component<IProps, any> {
  public state: IState = {
    isCreateModalOpen: false,
    isLoading: true,
    isAddLoading: false,
    isFilterOpen: false,
    outputFilter: "",
    isAdding: false,
    filteredOutputTags: this.props.outputTags || [],
  };

  public componentDidMount() {
    const { dispatch, clientId } = this.props;

    // Fetch output tags
    if (dispatch && clientId) {
      dispatch(getOutputTags(clientId) as any).then(() =>
        this.setState({ isLoading: false })
      );
    }
  }

  public componentDidUpdate(prevProps: IProps) {
    if (prevProps.outputTags !== this.props.outputTags) {
      this.setState({
        filteredOutputTags: this.props.outputTags,
        outputFilter: "",
      });
    }
  }

  private addOutput = (): void => this.setState({ isAdding: true });

  private handleFilterVisibility = () => {
    this.setState({
      isFilterOpen: !this.state.isFilterOpen,
    });
  };

  private handleOutputFilterChange = (
    event: React.FormEvent<HTMLInputElement>
  ) => {
    const outputFilter = event.currentTarget.value;
    const filteredOutputTags = this.props.outputTags.filter((tag) =>
      tag.Name.toLowerCase().includes(outputFilter.toLowerCase())
    );
    this.setState({
      outputFilter,
      filteredOutputTags,
    });
  };

  private handleEditCancel = (): void => this.setState({ isAdding: false });

  private isValidOutput = (outputTagName: string): boolean => {
    const { outputTags } = this.props;

    if ("" === outputTagName) {
      return false;
    }

    return !find(outputTags, (o) => toLower(o.Name) === toLower(outputTagName));
  };

  private handleEditSave = async (output: string): Promise<void> => {
    const { dispatch } = this.props;

    if (dispatch) {
      try {
        this.setState({ isAddLoading: true });
        await dispatch(createOutputTag(this.props.clientId, output) as any);
        this.setState({ isAdding: false, isAddLoading: false });
      } catch (error) {
        console.error("Error creating output tag:", error);
        this.setState({ isAddLoading: false });
      }
    }
  };

  public render() {
    const { clientId } = this.props;
    const {
      isAdding,
      isLoading,
      isAddLoading,
      outputFilter,
      filteredOutputTags,
    } = this.state;

    return (
      <>
        {isLoading ? (
          <Loading />
        ) : (
          <>
            <HTMLTable style={{ borderCollapse: "collapse", width: "100%" }}>
              <thead>
                <tr>
                  <th>
                    <div style={{ width: 160 }}>
                      <Flex justifyContent="space-between" alignItems="center">
                        <Text className={textClasses}>Output Tag Name</Text>
                        <Button
                          minimal={true}
                          icon={
                            this.state.isFilterOpen
                              ? IconNames.FILTER_OPEN
                              : IconNames.FILTER
                          }
                          onClick={this.handleFilterVisibility}
                          small={true}
                        />
                      </Flex>
                      {this.state.isFilterOpen && (
                        <Box mt={1}>
                          <InputGroup
                            small={true}
                            type="search"
                            leftIcon={IconNames.SEARCH}
                            placeholder="Filter Outputs"
                            value={outputFilter}
                            onChange={this.handleOutputFilterChange}
                            fill
                          />
                        </Box>
                      )}
                    </div>
                  </th>
                  <th>
                    <Box>
                      <Text className={textClasses}>Created By</Text>
                    </Box>
                  </th>
                  <th>
                    <Box>
                      <Text className={textClasses}>Created At</Text>
                    </Box>
                  </th>
                </tr>
              </thead>
              <tbody>
                {map(filteredOutputTags, (output, idx) => (
                  <OutputTagsRow
                    key={`${output.id}-${idx}`}
                    output={output}
                    clientId={clientId}
                  />
                ))}
              </tbody>
            </HTMLTable>
            <FloatingButton
              text="Define New Output Tag"
              onClick={this.addOutput}
            />
            {isAdding && (
              <AddOutputTagModal
                isOpen={true}
                isAddLoading={isAddLoading}
                isValid={this.isValidOutput}
                onCancel={this.handleEditCancel}
                onSave={this.handleEditSave}
              />
            )}
          </>
        )}
      </>
    );
  }
}

const mapStateToProps = (state: RootState) => ({
  outputTags: state.outputTags,
});

export default connect(mapStateToProps)(withRouter(OutputsTab));
